import { useEffect, useState, useMemo } from "react";
import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { trpc } from "../lib/trpc";
import { useLocation } from "wouter";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";

type FiltroTipo = "todos" | "orcamentos" | "pedidos" | "nfeAvulsa";

function formatDate(val: string | null | undefined): string {
  if (!val) return "—";
  const d = new Date(val);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("pt-BR");
}

function formatDateTime(val: string | null | undefined): string {
  if (!val) return "—";
  const d = new Date(val);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function ReviewQueue() {
  const { user, loading: authLoading } = useGoogleAuth();
  const [, setLocation] = useLocation();
  const utils = trpc.useUtils();

  // Filtros
  const [filtroTipo, setFiltroTipo] = useState<FiltroTipo>(() => {
    const tipo = new URLSearchParams(window.location.search).get("tipo");
    return tipo === "orcamentos" || tipo === "pedidos" || tipo === "nfeAvulsa" ? tipo : "todos";
  });
  const [busca, setBusca] = useState("");
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  const [arquivosDanfeSubstituta, setArquivosDanfeSubstituta] = useState<Record<number, File | null>>({});
  const [pedidoEnviandoDanfe, setPedidoEnviandoDanfe] = useState<number | null>(null);
  const [selecaoEmissaoAlternativa, setSelecaoEmissaoAlternativa] = useState<Record<number, { tabelaPrecoId: string; tipoOrcamentoId: string }>>({});
  const [pedidoEmitindoAlternativa, setPedidoEmitindoAlternativa] = useState<number | null>(null);
  const [emissoesAlternativasConcluidas, setEmissoesAlternativasConcluidas] = useState<Record<number, boolean>>({});

  // Auth redirect
  useEffect(() => {
    if (!authLoading && !user) {
      window.location.href = "/login";
    }
  }, [user, authLoading]);

  // Buscar orçamentos reprovados
  const { data: orcamentos = [], isLoading: loadingOrc, refetch: refetchOrc } = trpc.orcamentos.list.useQuery(
    { status: "reprovado" },
    { enabled: !!user }
  );

  // Buscar pedidos em revisão
  const { data: pedidosRevisao = [], isLoading: loadingPed, refetch: refetchPed } = trpc.pedidos.listRevisao.useQuery(
    undefined,
    { enabled: !!user }
  );

  const { data: nfeAvulsasRevisao = [], isLoading: loadingNfeAvulsa, refetch: refetchNfeAvulsa } = trpc.nfeAvulsa.listarRevisao.useQuery(
    undefined,
    { enabled: !!user }
  );

  const { data: configuracoesOrcamento = [] } = trpc.quoteTypes.list.useQuery(undefined, { enabled: !!user });
  const { data: tabelasPreco = [] } = trpc.priceTables.list.useQuery(undefined, { enabled: !!user });

  // Mutation reenviar para fila
  const mutReenviar = trpc.pedidos.reenviarParaFila.useMutation({
    onSuccess: () => {
      refetchPed();
      utils.pedidos.listOpen.invalidate();
    },
  });

  // Mutation processar pedido em revisão (retoma de onde parou)
  const mutProcessar = trpc.pedidos.processarRevisao.useMutation({
    onSuccess: () => {
      refetchPed();
      utils.pedidos.listOpen.invalidate();
    },
  });

  const mutRegistrarDanfeSubstituta = trpc.pedidos.registrarDanfeSubstitutaValorZero.useMutation({
    onSuccess: () => {
      refetchPed();
      utils.pedidos.listOpen.invalidate();
    },
  });

  const mutPrevisualizarEmissaoAlternativa = trpc.pedidos.previsualizarEmissaoAlternativaConfigurada.useMutation();
  const mutEmitirEmissaoAlternativa = trpc.pedidos.emitirNfeAlternativaConfigurada.useMutation({
    onSuccess: () => {
      refetchPed();
      utils.pedidos.listOpen.invalidate();
    },
  });

  const enviarDanfeSubstituta = async (pedidoId: number) => {
    const arquivo = arquivosDanfeSubstituta[pedidoId];
    if (!arquivo) {
      window.alert("Selecione a DANFE substituta em PDF.");
      return;
    }

    setPedidoEnviandoDanfe(pedidoId);
    try {
      const formData = new FormData();
      formData.append("file", arquivo);
      const respostaUpload = await fetch("/api/upload", { method: "POST", body: formData });
      const upload = await respostaUpload.json();
      if (!respostaUpload.ok) throw new Error(upload?.error || "Não foi possível enviar a DANFE substituta.");

      const dados = await mutRegistrarDanfeSubstituta.mutateAsync({
        pedidoId,
        arquivo: {
          nome: upload.name,
          url: upload.url,
          tamanho: upload.size,
          tipoMime: upload.mimeType,
        },
      });
      setArquivosDanfeSubstituta((atuais) => ({ ...atuais, [pedidoId]: null }));
      window.alert(`DANFE substituta registrada. NF-e ${dados.numeroNfe} · emissão ${dados.dataEmissao} · valor R$ ${dados.valorTotal.toFixed(2).replace(".", ",")}.`);
    } catch (erro: any) {
      window.alert(erro?.message || "Não foi possível registrar a DANFE substituta.");
    } finally {
      setPedidoEnviandoDanfe(null);
    }
  };

  const atualizarSelecaoEmissaoAlternativa = (pedidoId: number, campo: "tabelaPrecoId" | "tipoOrcamentoId", valor: string) => {
    setSelecaoEmissaoAlternativa((atuais) => {
      const atual = atuais[pedidoId] || { tabelaPrecoId: "", tipoOrcamentoId: "" };
      if (campo === "tipoOrcamentoId") {
        return { ...atuais, [pedidoId]: { ...atual, tipoOrcamentoId: valor, tabelaPrecoId: "" } };
      }
      return { ...atuais, [pedidoId]: { ...atual, [campo]: valor } };
    });
  };

  const emitirComOutroEmitente = async (pedidoId: number) => {
    const selecao = selecaoEmissaoAlternativa[pedidoId];
    const tabelaPrecoId = Number(selecao?.tabelaPrecoId);
    const tipoOrcamentoId = Number(selecao?.tipoOrcamentoId);
    if (!tabelaPrecoId || !tipoOrcamentoId) {
      window.alert("Selecione a configuração de orçamento e a tabela de preço.");
      return;
    }
    setPedidoEmitindoAlternativa(pedidoId);
    try {
      const previa = await mutPrevisualizarEmissaoAlternativa.mutateAsync({ pedidoId, tabelaPrecoId, tipoOrcamentoId });
      const itens = previa.itens.map((item: any) => `${item.codigo} · ${item.quantidade} × R$ ${item.valorUnitario.toFixed(2).replace(".", ",")}`).join("\n");
      const confirmado = window.confirm(
        `Emitir NF-e com outro emitente?\n\nPedido: #${pedidoId}\nEmitente: ${previa.emitenteNome}\nConfiguração: ${previa.tipoOrcamentoNome}\nTabela: ${previa.tabelaPrecoNome}\nValor da NF-e: R$ ${previa.valorTotal.toFixed(2).replace(".", ",")}\n\nItens:\n${itens}\n\nA confirmação transmitirá esta NF-e à SEFAZ. O pedido, estoque e configurações originais não serão alterados. Após a autorização, o pedido continuará em revisão até você clicar em Processar.`
      );
      if (!confirmado) return;
      const resultado = await mutEmitirEmissaoAlternativa.mutateAsync({ pedidoId, tabelaPrecoId, tipoOrcamentoId, assinatura: previa.assinatura });
      setEmissoesAlternativasConcluidas((atuais) => ({ ...atuais, [pedidoId]: true }));
      window.alert(`NF-e alternativa autorizada. NF-e ${resultado.numeroNfe} · chave ${resultado.chaveNfe} · valor R$ ${resultado.valorTotal.toFixed(2).replace(".", ",")}. O pedido permanece em revisão até você clicar em Processar.`);
    } catch (erro: any) {
      window.alert(erro?.message || "Não foi possível emitir a NF-e com outro emitente.");
    } finally {
      setPedidoEmitindoAlternativa(null);
    }
  };

  const mutNormalizarIcmsStNulo = trpc.pedidos.normalizarIcmsStNulo.useMutation({
    onSuccess: () => {
      refetchPed();
      utils.pedidos.listOpen.invalidate();
    },
  });

  const mutZerarCustoNulo = trpc.pedidos.zerarCustoNulo.useMutation({
    onSuccess: () => {
      refetchPed();
      utils.pedidos.listOpen.invalidate();
    },
  });

  const mutTrocarFreteParaNossoCarro = trpc.pedidos.trocarFreteParaNossoCarro.useMutation({
    onSuccess: () => {
      refetchPed();
      utils.pedidos.listOpen.invalidate();
    },
  });

  const mutRemoverTransportadoraFreteProprio = trpc.pedidos.removerTransportadoraFreteProprio.useMutation({
    onSuccess: () => {
      refetchPed();
      utils.pedidos.listOpen.invalidate();
    },
  });

  const mutLimitarRazaoSocialDestinatario60 = trpc.pedidos.limitarRazaoSocialDestinatario60.useMutation({
    onSuccess: () => {
      refetchPed();
      utils.pedidos.listOpen.invalidate();
    },
  });

  const mutRemoverCaracteresInvalidosDescricao = trpc.pedidos.removerCaracteresInvalidosDescricao.useMutation({
    onSuccess: () => {
      refetchPed();
      utils.pedidos.listOpen.invalidate();
    },
  });

  const mutNormalizarEndereco = trpc.pedidos.normalizarEndereco.useMutation({
    onSuccess: () => {
      refetchPed();
      utils.pedidos.listOpen.invalidate();
    },
  });

  const mutLimitarDescricao120 = trpc.pedidos.limitarDescricao120.useMutation({
    onSuccess: () => {
      refetchPed();
      utils.pedidos.listOpen.invalidate();
    },
  });

  const mutProcessarNfeAvulsa = trpc.nfeAvulsa.processarRevisao.useMutation({
    onSuccess: () => refetchNfeAvulsa(),
  });
  const mutRemoverNfeAvulsa = trpc.nfeAvulsa.removerRevisao.useMutation({
    onSuccess: () => refetchNfeAvulsa(),
  });
  const mutTrocarFreteNfeAvulsa = trpc.nfeAvulsa.trocarFreteParaNossoCarroRevisao.useMutation({
    onSuccess: () => refetchNfeAvulsa(),
  });
  const mutRemoverTransportadoraNfeAvulsa = trpc.nfeAvulsa.removerTransportadoraRevisao.useMutation({
    onSuccess: () => refetchNfeAvulsa(),
  });
  const mutLimitarRazaoSocialNfeAvulsa = trpc.nfeAvulsa.limitarRazaoSocialDestinatario60Revisao.useMutation({
    onSuccess: () => refetchNfeAvulsa(),
  });
  const mutRemoverCaracteresNfeAvulsa = trpc.nfeAvulsa.removerCaracteresInvalidosDescricaoRevisao.useMutation({
    onSuccess: () => refetchNfeAvulsa(),
  });
  const mutLimitarDescricaoNfeAvulsa = trpc.nfeAvulsa.limitarDescricao120Revisao.useMutation({
    onSuccess: () => refetchNfeAvulsa(),
  });
  const mutNormalizarEnderecoNfeAvulsa = trpc.nfeAvulsa.normalizarEnderecoRevisao.useMutation({
    onSuccess: () => refetchNfeAvulsa(),
  });

  // Unificar registros
  const registrosUnificados = useMemo(() => {
    const orc = (orcamentos as any[]).map((o) => ({
      tipo: "orcamento" as const,
      id: o.id,
      numero: o.numero,
      tipoNome: o.tipoOrcamentoNome || "—",
      emitenteId: o.emitenteId ?? null,
      emitenteNome: null as string | null,
      destinatarioId: o.destinatarioId ?? null,
      destinatarioNome: o.destinatarioNome || "—",
      motivo: o.anotacoes || "—",
      ultimaAlteracao: o.atualizadoEm || o.criadoEm,
      dataCriacao: o.criadoEm,
    }));

    const ped = (pedidosRevisao as any[]).map((p) => ({
      tipo: "pedido" as const,
      id: p.id,
      numero: p.id,
      tipoNome: "—",
      emitenteId: p.emitenteId ?? null,
      emitenteNome: p.emitenteNome || "—",
      destinatarioId: p.destinatarioId ?? null,
      destinatarioNome: p.destinatarioNome || "—",
      motivo: p.motivoRevisao || "—",
      ultimaAlteracao: p.atualizadoEm,
      dataCriacao: p.criadoEm,
      etapaFaturamento: p.etapaFaturamento ?? 0,
    }));

    const nfeAvulsa = (nfeAvulsasRevisao as any[]).map((nfe) => ({
      tipo: "nfeAvulsa" as const,
      id: nfe.id,
      numero: `${nfe.numeroNfe}/${nfe.serie}`,
      tipoNome: "NF-e avulsa",
      emitenteId: nfe.emitenteId ?? null,
      emitenteNome: nfe.emitenteNome || "—",
      destinatarioId: nfe.destinatarioId ?? null,
      destinatarioNome: nfe.destinatarioNome || "—",
      motivo: nfe.xMotivoAutorizacao || "—",
      ultimaAlteracao: nfe.atualizadoEm,
      dataCriacao: nfe.dataEmissao,
    }));

    return [...orc, ...ped, ...nfeAvulsa];
  }, [orcamentos, pedidosRevisao, nfeAvulsasRevisao]);

  // Aplicar filtros
  const registrosFiltrados = useMemo(() => {
    let lista = registrosUnificados;

    if (filtroTipo === "orcamentos") lista = lista.filter((r) => r.tipo === "orcamento");
    if (filtroTipo === "pedidos") lista = lista.filter((r) => r.tipo === "pedido");
    if (filtroTipo === "nfeAvulsa") lista = lista.filter((r) => r.tipo === "nfeAvulsa");

    if (busca.trim()) {
      const b = busca.trim().toLowerCase();
      lista = lista.filter((r) =>
        String(r.numero).includes(b) ||
        (r.destinatarioNome || "").toLowerCase().includes(b) ||
        (r.emitenteNome || "").toLowerCase().includes(b) ||
        (r.motivo || "").toLowerCase().includes(b)
      );
    }

    if (dataInicio) {
      const di = new Date(dataInicio);
      lista = lista.filter((r) => r.dataCriacao && new Date(r.dataCriacao) >= di);
    }
    if (dataFim) {
      const df = new Date(dataFim);
      df.setHours(23, 59, 59, 999);
      lista = lista.filter((r) => r.dataCriacao && new Date(r.dataCriacao) <= df);
    }

    return lista;
  }, [registrosUnificados, filtroTipo, busca, dataInicio, dataFim]);

  const isLoading = loadingOrc || loadingPed || loadingNfeAvulsa;

  if (authLoading || isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!user) return null;

  const totalOrc = (orcamentos as any[]).length;
  const totalPed = (pedidosRevisao as any[]).length;
  const totalNfeAvulsa = (nfeAvulsasRevisao as any[]).length;

  return (
    <div className="container mx-auto p-6">
      {/* Cabeçalho */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Fila de Revisão</h1>
        <p className="text-muted-foreground mt-1">
          Orçamentos reprovados, pedidos e NF-e avulsas em revisão aguardando resolução.
        </p>
      </div>

      {/* Filtros rápidos */}
      <div className="flex flex-wrap gap-2 mb-4">
        <Button
          size="sm"
          variant={filtroTipo === "todos" ? "default" : "outline"}
          onClick={() => setFiltroTipo("todos")}
        >
          Todos
          <span className="ml-1.5 bg-white/20 text-white rounded-full px-1.5 text-xs font-bold">
            {totalOrc + totalPed + totalNfeAvulsa}
          </span>
        </Button>
        <Button
          size="sm"
          variant={filtroTipo === "orcamentos" ? "default" : "outline"}
          onClick={() => setFiltroTipo("orcamentos")}
        >
          Orçamentos
          <span className="ml-1.5 rounded-full px-1.5 text-xs font-bold bg-red-100 text-red-800">
            {totalOrc}
          </span>
        </Button>
        <Button
          size="sm"
          variant={filtroTipo === "pedidos" ? "default" : "outline"}
          onClick={() => setFiltroTipo("pedidos")}
        >
          Pedidos
          <span className="ml-1.5 rounded-full px-1.5 text-xs font-bold bg-orange-100 text-orange-800">
            {totalPed}
          </span>
        </Button>
        <Button
          size="sm"
          variant={filtroTipo === "nfeAvulsa" ? "default" : "outline"}
          onClick={() => setFiltroTipo("nfeAvulsa")}
        >
          NF-e avulsa
          <span className="ml-1.5 rounded-full px-1.5 text-xs font-bold bg-purple-100 text-purple-800">
            {totalNfeAvulsa}
          </span>
        </Button>
      </div>

      {/* Busca e filtro de data */}
      <div className="flex flex-wrap gap-2 mb-4">
        <Input
          placeholder="Buscar por número, destinatário, emitente ou motivo..."
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          className="max-w-sm"
        />
        <div className="flex items-center gap-1">
          <span className="text-sm text-muted-foreground">De:</span>
          <Input
            type="date"
            value={dataInicio}
            onChange={(e) => setDataInicio(e.target.value)}
            className="w-36"
          />
        </div>
        <div className="flex items-center gap-1">
          <span className="text-sm text-muted-foreground">Até:</span>
          <Input
            type="date"
            value={dataFim}
            onChange={(e) => setDataFim(e.target.value)}
            className="w-36"
          />
        </div>
        {(busca || dataInicio || dataFim) && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => { setBusca(""); setDataInicio(""); setDataFim(""); }}
          >
            Limpar filtros
          </Button>
        )}
      </div>

      {/* Tabela */}
      {registrosFiltrados.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          {registrosUnificados.length === 0
            ? "Nenhum item na fila de revisão."
            : "Nenhum resultado para os filtros aplicados."}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Tipo</th>
                <th className="px-3 py-2 text-left font-medium">Número</th>
                <th className="px-3 py-2 text-left font-medium">Tipo Doc.</th>
                <th className="px-3 py-2 text-left font-medium">Emitente</th>
                <th className="px-3 py-2 text-left font-medium">Destinatário</th>
                <th className="px-3 py-2 text-left font-medium">Motivo / Erro</th>
                <th className="px-3 py-2 text-left font-medium">Última Alteração</th>
                <th className="px-3 py-2 text-left font-medium">Criação</th>
                <th className="px-3 py-2 text-left font-medium">Ação</th>
              </tr>
            </thead>
            <tbody>
              {registrosFiltrados.map((r) => (
                <tr key={`${r.tipo}-${r.id}`} className="border-t hover:bg-muted/30 transition-colors">
                  <td className="px-3 py-2">
                    {r.tipo === "orcamento" ? (
                      <Badge variant="destructive" className="text-xs">Orçamento</Badge>
                    ) : r.tipo === "nfeAvulsa" ? (
                      <Badge className="text-xs bg-purple-100 text-purple-800 hover:bg-purple-100">NF-e avulsa</Badge>
                    ) : (
                      <Badge className="text-xs bg-orange-100 text-orange-800 hover:bg-orange-100">Pedido</Badge>
                    )}
                  </td>
                  <td className="px-3 py-2 font-mono font-medium">
                    {r.tipo === "orcamento" ? (
                      <button
                        className="text-blue-600 hover:underline font-mono"
                        onClick={() => setLocation(`/orcamentos/editar/${r.id}`)}
                        title="Abrir orçamento"
                      >
                        #{r.numero}
                      </button>
                    ) : (
                      <span className="font-mono">#{r.numero}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground text-xs">{r.tipoNome}</td>
                  <td className="px-3 py-2">
                    {r.emitenteId ? (
                      <button
                        className="text-blue-600 hover:underline text-left"
                        onClick={() => setLocation(`/emitentes`)}
                        title={`Ver emitente: ${r.emitenteNome}`}
                      >
                        {r.emitenteNome || "—"}
                      </button>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {r.destinatarioId ? (
                      <button
                        className="text-blue-600 hover:underline text-left"
                        onClick={() => setLocation(`/recipients`)}
                        title={`Ver destinatário: ${r.destinatarioNome}`}
                      >
                        {r.destinatarioNome || "—"}
                      </button>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 max-w-xs">
                    <span
                      className="block truncate text-xs text-red-700"
                      title={r.motivo}
                    >
                      {r.motivo}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">
                    {formatDateTime(r.ultimaAlteracao)}
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">
                    {formatDate(r.dataCriacao)}
                  </td>
                  <td className="px-3 py-2">
                    {r.tipo === "orcamento" ? (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-xs"
                        onClick={() => setLocation(`/orcamentos/editar/${r.id}`)}
                      >
                        Editar Orçamento
                      </Button>
	                    ) : r.tipo === "nfeAvulsa" ? (
	                      <div className="flex flex-wrap gap-2">
	                        <Button
	                          size="sm"
	                          variant="outline"
	                          className="text-xs"
	                          disabled={mutProcessarNfeAvulsa.isPending}
	                          onClick={() => mutProcessarNfeAvulsa.mutate({ id: r.id })}
	                          title="Remontar e transmitir esta NF-e avulsa com os dados atuais"
	                        >
	                          {mutProcessarNfeAvulsa.isPending ? "Processando..." : "Processar"}
	                        </Button>
	                        <Button
	                          size="sm"
	                          variant="outline"
	                          className="text-xs"
	                          disabled={mutRemoverNfeAvulsa.isPending}
	                          onClick={() => mutRemoverNfeAvulsa.mutate({ id: r.id })}
	                          title="Alterar somente o status desta NF-e avulsa para cancelada"
	                        >
	                          {mutRemoverNfeAvulsa.isPending ? "Removendo..." : "Remover"}
	                        </Button>
	                        {r.motivo.includes("Tipo de frete 'SEM_TRANSPORTE'") && r.motivo.includes("não mapeado para modFrete") && (
	                          <Button
	                            size="sm"
	                            variant="outline"
	                            className="text-xs"
	                            disabled={mutTrocarFreteNfeAvulsa.isPending}
	                            onClick={() => mutTrocarFreteNfeAvulsa.mutate({ id: r.id })}
	                            title="Trocar somente o frete SEM_TRANSPORTE desta NF-e avulsa para NOSSO_CARRO"
	                          >
	                            {mutTrocarFreteNfeAvulsa.isPending ? "Corrigindo..." : "Trocar para nosso carro"}
	                          </Button>
	                        )}
	                        {r.motivo.includes("Rejeição: Transporte próprio por conta do Remetente e CNPJ Base ou CPF do Transportador difere do CNPJ Base ou CPF do Remetente") && (
	                          <Button
	                            size="sm"
	                            variant="outline"
	                            className="text-xs"
	                            disabled={mutRemoverTransportadoraNfeAvulsa.isPending}
	                            onClick={() => mutRemoverTransportadoraNfeAvulsa.mutate({ id: r.id })}
	                            title="Remover somente a transportadora vinculada ao frete próprio desta NF-e avulsa"
	                          >
	                            {mutRemoverTransportadoraNfeAvulsa.isPending ? "Corrigindo..." : "Remover transportadora"}
	                          </Button>
	                        )}
	                        {r.motivo.includes("cvc-maxLength-valid") && r.motivo.includes("maxLength '60'") && r.motivo.includes("#AnonType_xNomedestinfNFeTNFe") && (
	                          <Button
	                            size="sm"
	                            variant="outline"
	                            className="text-xs"
	                            disabled={mutLimitarRazaoSocialNfeAvulsa.isPending}
	                            onClick={() => mutLimitarRazaoSocialNfeAvulsa.mutate({ id: r.id })}
	                            title="Limitar a razão social atual do destinatário a 60 caracteres"
	                          >
	                            {mutLimitarRazaoSocialNfeAvulsa.isPending ? "Corrigindo..." : "Limitar nome a 60"}
	                          </Button>
	                        )}
	                        {r.motivo.includes("cvc-pattern-valid") && r.motivo.includes("Descrição:") && r.motivo.includes("#AnonType_xProdproddetinfNFeTNFe") && (
	                          <Button
	                            size="sm"
	                            variant="outline"
	                            className="text-xs"
	                            disabled={mutRemoverCaracteresNfeAvulsa.isPending}
	                            onClick={() => mutRemoverCaracteresNfeAvulsa.mutate({ id: r.id })}
	                            title="Remover somente caracteres inválidos das descrições atuais de origem"
	                          >
	                            {mutRemoverCaracteresNfeAvulsa.isPending ? "Corrigindo..." : "Remover caracteres inválidos"}
	                          </Button>
	                        )}
	                        {r.motivo.includes("cvc-pattern-valid") && r.motivo.includes("#AnonType_nroTEndereco") && (
	                          <Button
	                            size="sm"
	                            variant="outline"
	                            className="text-xs"
	                            disabled={mutNormalizarEnderecoNfeAvulsa.isPending}
	                            onClick={() => mutNormalizarEnderecoNfeAvulsa.mutate({ id: r.id })}
	                            title="Normalizar somente o endereço atual do destinatário"
	                          >
	                            {mutNormalizarEnderecoNfeAvulsa.isPending ? "Normalizando..." : "Normalizar endereço"}
	                          </Button>
	                        )}
	                        {r.motivo.includes("cvc-maxLength-valid") && r.motivo.includes("maxLength '120'") && r.motivo.includes("Descrição:") && r.motivo.includes("#AnonType_xProdproddetinfNFeTNFe") && (
	                          <Button
	                            size="sm"
	                            variant="outline"
	                            className="text-xs"
	                            disabled={mutLimitarDescricaoNfeAvulsa.isPending}
	                            onClick={() => mutLimitarDescricaoNfeAvulsa.mutate({ id: r.id })}
	                            title="Limitar somente as descrições atuais de origem a 120 caracteres"
	                          >
	                            {mutLimitarDescricaoNfeAvulsa.isPending ? "Corrigindo..." : "Limitar descrição a 120"}
	                          </Button>
	                        )}
	                      </div>
                    ) : r.etapaFaturamento > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs"
                          disabled={mutProcessar.isPending}
                          onClick={() => mutProcessar.mutate({ pedidoId: r.id })}
                          title="Retomar processamento de onde parou"
                        >
                          {mutProcessar.isPending ? "Processando..." : "Processar"}
                        </Button>
                        {r.motivo.includes("NF-e de venda com valor zerado") && (
                          <div className="flex items-center gap-2">
                            <Input
                              type="file"
                              accept="application/pdf,.pdf"
                              className="h-8 w-56 text-xs"
                              onChange={(evento) => setArquivosDanfeSubstituta((atuais) => ({
                                ...atuais,
                                [r.id]: evento.target.files?.[0] || null,
                              }))}
                            />
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-xs"
                              disabled={pedidoEnviandoDanfe === r.id || !arquivosDanfeSubstituta[r.id]}
                              onClick={() => enviarDanfeSubstituta(r.id)}
                              title="Anexar DANFE substituta para nota com valor zerado"
                            >
                              {pedidoEnviandoDanfe === r.id ? "Validando..." : "Anexar DANFE substituta"}
                            </Button>
                          </div>
                        )}
                        {r.motivo.includes("NF-e de venda com valor zerado") && !emissoesAlternativasConcluidas[r.id] && (() => {
                          const selecao = selecaoEmissaoAlternativa[r.id] || { tabelaPrecoId: "", tipoOrcamentoId: "" };
                          const configuracaoSelecionada = (configuracoesOrcamento as any[]).find((configuracao) => Number(configuracao.id) === Number(selecao.tipoOrcamentoId));
                          const configuracoesAlternativas = (configuracoesOrcamento as any[]).filter((configuracao) => Number(configuracao.emitenteId) !== Number(r.emitenteId));
                          const tabelasDoEmitente = configuracaoSelecionada
                            ? (tabelasPreco as any[]).filter((tabela) => Number(tabela.emitenteId) === Number(configuracaoSelecionada.emitenteId))
                            : [];
                          return (
                            <div className="flex flex-wrap items-center gap-2 border-l pl-2">
                              <select
                                className="h-8 rounded-md border bg-background px-2 text-xs"
                                value={selecao.tipoOrcamentoId}
                                onChange={(evento) => atualizarSelecaoEmissaoAlternativa(r.id, "tipoOrcamentoId", evento.target.value)}
                                aria-label="Configuração de orçamento para emissão alternativa"
                              >
                                <option value="">Configuração</option>
                                {configuracoesAlternativas.map((configuracao) => (
                                  <option key={configuracao.id} value={configuracao.id}>{configuracao.nome}</option>
                                ))}
                              </select>
                              <select
                                className="h-8 rounded-md border bg-background px-2 text-xs"
                                value={selecao.tabelaPrecoId}
                                onChange={(evento) => atualizarSelecaoEmissaoAlternativa(r.id, "tabelaPrecoId", evento.target.value)}
                                disabled={!configuracaoSelecionada}
                                aria-label="Tabela de preço para emissão alternativa"
                              >
                                <option value="">Tabela de preço</option>
                                {tabelasDoEmitente.map((tabela) => (
                                  <option key={tabela.id} value={tabela.id}>{tabela.nome}</option>
                                ))}
                              </select>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-xs"
                                disabled={pedidoEmitindoAlternativa === r.id}
                                onClick={() => emitirComOutroEmitente(r.id)}
                                title="Simular e emitir NF-e com configuração e tabela selecionadas"
                              >
                                {pedidoEmitindoAlternativa === r.id ? "Emitindo..." : "Emitir com outro emitente"}
                              </Button>
                            </div>
                          );
                        })()}
                        {r.motivo.includes("NF-e de venda com valor zerado") && emissoesAlternativasConcluidas[r.id] && (
                          <span className="text-xs font-medium text-green-700">NF-e alternativa registrada; clique em Processar.</span>
                        )}
                        {r.motivo.includes("icmsStValor nulo") && r.emitenteId && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs"
                            disabled={mutNormalizarIcmsStNulo.isPending}
                            onClick={() => mutNormalizarIcmsStNulo.mutate({ pedidoId: r.id, emitenteId: r.emitenteId })}
                            title="Substituir somente ICMS-ST nulo por zero neste pedido e emitente"
                          >
                            {mutNormalizarIcmsStNulo.isPending ? "Normalizando..." : "Zerar ST nulo"}
                          </Button>
                        )}
                        {r.motivo.includes("Rejeição: Total do Produto / Serviço difere do somatório dos itens") && r.emitenteId && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs"
                            disabled={mutZerarCustoNulo.isPending}
                            onClick={() => mutZerarCustoNulo.mutate({ pedidoId: r.id, emitenteId: r.emitenteId })}
                            title="Substituir somente custos nulos por zero neste pedido e emitente"
                          >
                            {mutZerarCustoNulo.isPending ? "Corrigindo..." : "Zerar custo nulo"}
                          </Button>
                        )}
                        {r.motivo.includes("Tipo de frete 'SEM_TRANSPORTE'") && r.motivo.includes("não mapeado para modFrete") && r.emitenteId && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs"
                            disabled={mutTrocarFreteParaNossoCarro.isPending}
                            onClick={() => mutTrocarFreteParaNossoCarro.mutate({ pedidoId: r.id, emitenteId: r.emitenteId })}
                            title="Trocar somente o frete SEM_TRANSPORTE deste pedido para NOSSO_CARRO"
                          >
                            {mutTrocarFreteParaNossoCarro.isPending ? "Corrigindo..." : "Trocar para nosso carro"}
                          </Button>
                        )}
                        {r.motivo.includes("Rejeição: Transporte próprio por conta do Remetente e CNPJ Base ou CPF do Transportador difere do CNPJ Base ou CPF do Remetente") && r.emitenteId && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs"
                            disabled={mutRemoverTransportadoraFreteProprio.isPending}
                            onClick={() => mutRemoverTransportadoraFreteProprio.mutate({ pedidoId: r.id, emitenteId: r.emitenteId })}
                            title="Remover somente a transportadora vinculada ao frete próprio por conta do remetente"
                          >
                            {mutRemoverTransportadoraFreteProprio.isPending ? "Corrigindo..." : "Remover transportadora"}
                          </Button>
                        )}
                        {r.motivo.includes("cvc-maxLength-valid") && r.motivo.includes("maxLength '60'") && r.motivo.includes("#AnonType_xNomedestinfNFeTNFe") && r.emitenteId && r.destinatarioId && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs"
                            disabled={mutLimitarRazaoSocialDestinatario60.isPending}
                            onClick={() => mutLimitarRazaoSocialDestinatario60.mutate({ pedidoId: r.id, emitenteId: r.emitenteId })}
                            title="Limitar a razão social do destinatário a 60 caracteres"
                          >
                            {mutLimitarRazaoSocialDestinatario60.isPending ? "Corrigindo..." : "Limitar nome a 60"}
                          </Button>
                        )}
                        {r.motivo.includes("cvc-pattern-valid") && r.motivo.includes("Descrição:") && r.motivo.includes("#AnonType_xProdproddetinfNFeTNFe") && r.emitenteId && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs"
                            disabled={mutRemoverCaracteresInvalidosDescricao.isPending}
                            onClick={() => mutRemoverCaracteresInvalidosDescricao.mutate({ pedidoId: r.id, emitenteId: r.emitenteId })}
                            title="Remover somente caracteres inválidos das descrições deste pedido"
                          >
                            {mutRemoverCaracteresInvalidosDescricao.isPending ? "Corrigindo..." : "Remover caracteres inválidos"}
                          </Button>
                        )}
                        {r.motivo.includes("cvc-pattern-valid") && r.motivo.includes("#AnonType_nroTEndereco") && r.emitenteId && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs"
                            disabled={mutNormalizarEndereco.isPending}
                            onClick={() => mutNormalizarEndereco.mutate({ pedidoId: r.id, emitenteId: r.emitenteId })}
                            title="Normalizar somente o endereço de entrega deste pedido"
                          >
                            {mutNormalizarEndereco.isPending ? "Normalizando..." : "Normalizar endereço"}
                          </Button>
                        )}
                        {r.motivo.includes("cvc-maxLength-valid") && r.motivo.includes("maxLength '120'") && r.motivo.includes("Descrição:") && r.motivo.includes("#AnonType_xProdproddetinfNFeTNFe") && r.emitenteId && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs"
                            disabled={mutLimitarDescricao120.isPending}
                            onClick={() => mutLimitarDescricao120.mutate({ pedidoId: r.id, emitenteId: r.emitenteId })}
                            title="Limitar somente descrições acima de 120 caracteres neste pedido"
                          >
                            {mutLimitarDescricao120.isPending ? "Corrigindo..." : "Limitar descrição a 120"}
                          </Button>
                        )}
                      </div>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-xs"
                        disabled={mutReenviar.isPending}
                        onClick={() => mutReenviar.mutate({ pedidoId: r.id })}
                        title="Reenviar pedido para a fila de conferência"
                      >
                        {mutReenviar.isPending ? "Reenviando..." : "Enviar para Conferência"}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
